import React, { useState, useEffect, useMemo } from "react";
import { Input, Select, Textarea, FormField, Button, SectionTitle, StatusBadge, Modal } from "../../../SharedInputs.jsx";
import { DollarSign, Calendar, BarChart2, Mail, CheckCircle } from "lucide-react";
import { InstallmentCard } from "./InstalmentCard.jsx";
import { getAgentByName } from "../../../../services/AgentService.js";
import { getFirstCourseByName } from "../../../../services/CourseService.js";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { v4 as uuidv4 } from "uuid";
import { updateStudentFeesPlan } from "../../../../services/StudentRecordService.js";
import { fileStorageService } from "../../../../services/FileStorageService.js";
import { generateInitialDepositInvoice, generateInstallmentInvoice, generateBatchPendingInvoice, generateInstallmentReceipt, generateBatchCombinedReceipt } from "../../../../services/InvoiceService.js";
const calculateCommission = (amount, rate) =>
  ((parseFloat(amount || 0) * parseFloat(rate || 0)) / 100).toFixed(2);

export function SectionFeesPlan({ onCancel, initialData = {}, course = {}, isCustomPackage = false }) {

  const courseFeePlan = course?.feesPlan || {};
  const [activeTab, setActiveTab] = useState("summary");
  const [feesPlan, setFeesPlan] = useState({
    commissionRate: courseFeePlan.commissionRate || "",
    enrollmentFees: courseFeePlan.enrollmentFees || "",
    materialFees: courseFeePlan.materialFees || "",
    tuitionFees: courseFeePlan.tuitionFees || "",
    additionalFees: courseFeePlan.additionalFees || "",
    oshcFees: courseFeePlan.oshcFees || "",
    totalFees: courseFeePlan.totalFees || 0,
    numOfInstallments: courseFeePlan.numOfInstallments || 1,
    initialDeposit: courseFeePlan.initialDeposit || { depositAmount: "", dueDate: "" },
    installments: courseFeePlan.installments || [],
    editedIndexes: courseFeePlan.editedIndexes || [],
    customInstallments: courseFeePlan.customInstallments || false,
    invoiceHistory: courseFeePlan.invoiceHistory || [],
    receiptHistory: courseFeePlan.receiptHistory || [],
  });

  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [frequency, setFrequency] = useState("monthly");
  const [preview, setPreview] = useState([]);
  const [loading, setLoading] = useState(false);
  // Pending dues state
  const [sendPendingDuesMode, setSendPendingDuesMode] = useState(false);
  const [pendingDuesSelection, setPendingDuesSelection] = useState([]);
  // Pending dues with fine state
  const FINE_AMOUNT_PER_WEEK = 100;
  const [sendPendingDuesFineMode, setSendPendingDuesFineMode] = useState(false);
  const [pendingDuesFineSelection, setPendingDuesFineSelection] = useState([]);

  // Fine mode applies to the full selected invoice, not to every row as a separate standard fine.
  // weekly   = calculate fine per selected instalment, then add ONE combined Late Fine line at the end.
  // standard = add ONE Standard Late Fine line at the end.
  // none     = no fine line.
  const [fineMode, setFineMode] = useState("weekly");
  const [standardLateFineAmount, setStandardLateFineAmount] = useState(100);
  const [selectedFineKeys, setSelectedFineKeys] = useState([]);

  // Combined receipt state
  const [sendCombinedReceiptMode, setSendCombinedReceiptMode] = useState(false);
  const [combinedReceiptSelection, setCombinedReceiptSelection] = useState([]);
  const getInvoiceSentMeta = (installment) => {
    const invoiceSent = !!installment?.invoiceSent;

    const sentWithFine =
      invoiceSent &&
      (
        Number(installment?.fineAmount || 0) > 0 ||
        installment?.fineSelected === true ||
        installment?.fineMode === "standard"
      );

    return {
      invoiceSent,
      sentWithFine,
      color: !invoiceSent
        ? "#9ca3af"
        : sentWithFine
          ? "#9f3a38"
          : "#059669",
      label: !invoiceSent
        ? "Not sent"
        : sentWithFine
          ? "Sent with fine"
          : "Sent",
    };
  };

  const InvoiceSentIndicator = ({ installment }) => {
    const meta = getInvoiceSentMeta(installment);

    if (!meta.invoiceSent) {
      return (
        <span style={{ fontSize: 12, color: meta.color }}>
          Not sent
        </span>
      );
    }

    return (
      <span
        style={{
          fontSize: 12,
          color: meta.color,
          fontWeight: 700,
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
        }}
      >
        <CheckCircle
          style={{
            width: 13,
            height: 13,
            color: meta.color,
          }}
        />
        {meta.label}
      </span>
    );
  };

  // --- Eligible items for batch receipt ---
  // Includes only Paid items where invoice is already generated/added in system
  const eligibleForReceiptBatch = useMemo(() => {
    const results = [];

    const isPaid = (item) =>
      String(item?.status || "").toLowerCase() === "paid";

    const hasInvoice = (item) =>
      !!item?.invoiceNumber || !!item?.invoiceId || !!item?.studentInvoiceId;

    const hasAmount = (item) =>
      Number(item?.depositAmount || item?.amount || 0) > 0;

    // Initial Deposit
    const initialDeposit = feesPlan?.initialDeposit;

    if (
      initialDeposit &&
      isPaid(initialDeposit) &&
      hasAmount(initialDeposit)
    ) {
      results.push({
        key: "initial",
        type: "initial",
        label: "Initial Deposit",
        installment: initialDeposit,
        amount: Number(initialDeposit.depositAmount || initialDeposit.amount || 0),
        invoiceNumber: initialDeposit.invoiceNumber || "",
        invoiceId: initialDeposit.invoiceId || initialDeposit.studentInvoiceId || "",
        paidDate: initialDeposit.paidDate || initialDeposit.paymentDate || "",
      });
    }

    // Instalments
    (feesPlan?.installments || []).forEach((inst, idx) => {
      if (
        isPaid(inst) &&
        hasAmount(inst)
      ) {
        results.push({
          key: `inst-${idx}`,
          type: "installment",
          idx,
          label: `Instalment #${idx + 1}`,
          installment: inst,
          amount: Number(inst.depositAmount || inst.amount || 0),
          invoiceNumber: inst.invoiceNumber || "",
          invoiceId: inst.invoiceId || inst.studentInvoiceId || "",
          paidDate: inst.paidDate || inst.paymentDate || "",
        });
      }
    });

    return results;
  }, [feesPlan]);
  
  const batchTotalForReceipt = eligibleForReceiptBatch
    .filter((e) => combinedReceiptSelection.includes(e.key))
    .reduce((sum, e) => sum + Number(e.amount || 0), 0);
  
  const selectedReceiptsForBatch = eligibleForReceiptBatch.filter((e) =>
    combinedReceiptSelection.includes(e.key)
  );

  // --- Eligible items for batch invoice ---
  // Includes: all Overdue, all Pending past/today, + next single upcoming unpaid
  const eligibleForBatch = useMemo(() => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const results = [];

    // Initial deposit
    const initDue = feesPlan.initialDeposit.dueDate
      ? new Date(feesPlan.initialDeposit.dueDate) : null;
    if (initDue && feesPlan.initialDeposit.status !== "Paid") {
      const initDueNorm = new Date(initDue); initDueNorm.setHours(0, 0, 0, 0);
      results.push({
        key: "initial",
        label: "Initial Deposit",
        installment: feesPlan.initialDeposit,
        isUpcoming: initDueNorm > today,
      });
    }

    // Installments — past/today all included, future only next one
    let nextUpcomingFound = false;
    const sorted = [...feesPlan.installments]
      .map((inst, idx) => ({ inst, idx }))
      .sort((a, b) => new Date(a.inst.dueDate) - new Date(b.inst.dueDate));

    sorted.forEach(({ inst, idx }) => {
      if (inst.status === "Paid") return;
      const due = inst.dueDate ? new Date(inst.dueDate) : null;
      if (!due) return;
      const dueNorm = new Date(due); dueNorm.setHours(0, 0, 0, 0);
      const isUpcoming = dueNorm > today;

      if (!isUpcoming) {
        results.push({
          key: `inst-${idx}`,
          idx,
          label: `Instalment #${idx + 1}`,
          installment: inst,
          isUpcoming: false,
        });
      } else if (!nextUpcomingFound) {
        results.push({
          key: `inst-${idx}`,
          idx,
          label: `Instalment #${idx + 1}`,
          installment: inst,
          isUpcoming: true,
        });
        nextUpcomingFound = true;
      }
    });

    return results;
  }, [feesPlan]);
  const batchTotal = eligibleForBatch
    .filter(e => pendingDuesSelection.includes(e.key))
    .reduce((s, e) => s + parseFloat(e.installment.depositAmount || 0), 0);

  const parseLocalDate = (dateStr) => {
    if (!dateStr) return null;

    const [year, month, day] = String(dateStr).split("-").map(Number);

    if (!year || !month || !day) return null;

    return new Date(year, month - 1, day);
  };

  const getLateWeeks = (dueDate) => {
    const due = parseLocalDate(dueDate);

    if (!due) return 0;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    due.setHours(0, 0, 0, 0);

    const diffMs = today.getTime() - due.getTime();
    const daysLate = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (daysLate <= 0) return 0;

    return Math.ceil(daysLate / 7);
  };

  const getPendingAmount = (installment) => {
    const installmentAmount = parseFloat(installment?.depositAmount || 0);
    const amountPaid = parseFloat(installment?.amountPaid || 0);

    return Math.max(installmentAmount - amountPaid, 0);
  };

  const isFineSelected = (key) => selectedFineKeys.includes(key);

  const toggleFineForKey = (key, checked) => {
    setSelectedFineKeys((prev) => {
      if (checked) {
        return prev.includes(key) ? prev : [...prev, key];
      }

      return prev.filter((selectedKey) => selectedKey !== key);
    });
  };

  const getWeeklyFineAmountForItem = (item) => {
    if (fineMode !== "weekly") return 0;
    if (!isFineSelected(item.key)) return 0;

    const lateWeeks = getLateWeeks(item.installment?.dueDate);
    return Number((lateWeeks * FINE_AMOUNT_PER_WEEK).toFixed(2));
  };

  const eligibleForBatchWithFine = useMemo(() => {
    return eligibleForBatch.map((item) => {
      const pendingAmount = getPendingAmount(item.installment);
      const lateWeeks = getLateWeeks(item.installment?.dueDate);
      const weeklyFineAmount = getWeeklyFineAmountForItem(item);
      const totalWithItemFine = pendingAmount + weeklyFineAmount;

      return {
        ...item,
        pendingAmount,
        lateWeeks,
        weeklyFineAmount,
        fineAmount: weeklyFineAmount,
        totalWithItemFine,
        fineSelected: isFineSelected(item.key),
        fineExcluded: !isFineSelected(item.key),
      };
    });
  }, [eligibleForBatch, fineMode, selectedFineKeys]);

  const selectedPendingFineItems = eligibleForBatchWithFine.filter((e) =>
    pendingDuesFineSelection.includes(e.key)
  );

  const batchPendingSubtotal = selectedPendingFineItems.reduce(
    (sum, e) => sum + Number(e.pendingAmount || 0),
    0
  );

  const batchWeeklyFineTotal = selectedPendingFineItems.reduce(
    (sum, e) => sum + Number(e.weeklyFineAmount || 0),
    0
  );

  const batchFineTotal =
    fineMode === "standard"
        ? Number(parseFloat(standardLateFineAmount || 0).toFixed(2))
        : batchWeeklyFineTotal;

  const batchPendingWithFineTotal = batchPendingSubtotal + batchFineTotal;

  const weeklyFineBreakdown = selectedPendingFineItems
    .filter((e) => Number(e.weeklyFineAmount || 0) > 0)
    .map(
      (e) =>
        `${e.label}: ${e.lateWeeks} week${e.lateWeeks > 1 ? "s" : ""} × $${FINE_AMOUNT_PER_WEEK} = $${Number(e.weeklyFineAmount || 0).toFixed(2)}`
    );

  const getFinalFineLineDescription = () => {

    if (fineMode === "standard") {
      return "Standard late fine amount";
    }

    return weeklyFineBreakdown.length > 0
      ? weeklyFineBreakdown.join(";\n")
      : `Late fine calculated at $${FINE_AMOUNT_PER_WEEK} per late week`;
  };

  // --- Batch invoice handler ---
  const handleSendPendingDuesInvoice = async () => {
    const selected = eligibleForBatch.filter(e => pendingDuesSelection.includes(e.key));
    if (selected.length === 0) return;

    if (!initialData.formState?.contactID) {
      alert("Student contactID is missing. Please sync student with Axcelerate first.");
      return;
    }

    setLoading(true);
    try {
      const studentData = {
        contactID: initialData.formState.contactID,
        studentId: initialData.formState.studentId,
        firstName: initialData.formState.firstName,
        lastName: initialData.formState.lastName,
        email: initialData.formState.email,
        address: initialData.formState.onshoreAddress,
      };

      const lineItems = selected.map((e, i) => ({
        lineNumber: i + 1,
        label: e.label,
        amount: parseFloat(e.installment.depositAmount || 0),
        dueDate: e.installment.dueDate,
        status: e.installment.status || "Pending",
        isUpcoming: e.isUpcoming,
      }));

      const result = await generateBatchPendingInvoice(
        studentData,
        { ...course, feesPlan },
        lineItems
      );

      if (result.success) {
        const sentDate = new Date().toISOString().split("T")[0];
        const updatedPlan = structuredClone(feesPlan);

        selected.forEach(e => {
          if (e.key === "initial") {
            updatedPlan.initialDeposit = {
              ...updatedPlan.initialDeposit,
              invoiceNumber: result.invoiceNumber,
              invoiceSent: true,
              invoiceSentDate: sentDate,
            };
          } else {
            updatedPlan.installments[e.idx] = {
              ...updatedPlan.installments[e.idx],
              invoiceNumber: result.invoiceNumber,
              invoiceSent: true,
              invoiceSentDate: sentDate,
            };
          }
        });
        const historyEntry = {
          invoiceNumber: result.invoiceNumber,
          sentDate,
          type: "combined",
          items: selected.map((e) => ({
            key: e.key,
            label: e.label,
            amount: parseFloat(e.installment.depositAmount || 0),
            dueDate: e.installment.dueDate,
            status: e.installment.status || "Pending",
          })),
        };

        updatedPlan.invoiceHistory = [...(updatedPlan.invoiceHistory || []), historyEntry];

        setFeesPlan(updatedPlan);
        setSendPendingDuesMode(false);
        setPendingDuesSelection([]);
        setSendCombinedReceiptMode(false);
        setCombinedReceiptSelection([]);
        await handleSave(updatedPlan);
        alert(`Combined invoice ${result.invoiceNumber} sent for ${selected.length} item(s)!`);
      } else {
        alert(`Failed: ${result.error}`);
      }
    } catch (err) {
      console.error("Batch invoice error:", err);
      alert(`Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };
  const getFineDueDate = () => {
    const date = new Date();
    date.setDate(date.getDate() + 7);

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };
  const handleSendPendingDuesWithFineInvoice = async () => {
    const selected = eligibleForBatchWithFine.filter((e) =>
      pendingDuesFineSelection.includes(e.key)
    );

    if (selected.length === 0) return;

    if (!initialData.formState?.contactID) {
      alert("Student contactID is missing. Please sync student with Axcelerate first.");
      return;
    }

    setLoading(true);

    try {
      const studentData = {
        contactID: initialData.formState.contactID,
        studentId: initialData.formState.studentId,
        firstName: initialData.formState.firstName,
        lastName: initialData.formState.lastName,
        email: initialData.formState.email,
        address: initialData.formState.onshoreAddress,
      };

      const pendingLineItems = selected.map((e, index) => ({
        lineNumber: index + 1,
        label: `${e.label} - Pending Instalment`,
        description: `Pending amount for due date ${e.installment.dueDate || "-"}`,
        amount: Number(e.pendingAmount || 0),
        dueDate: e.installment.dueDate,
        status: e.installment.status || "Pending",
        isUpcoming: e.isUpcoming,
        lineType: "pending_instalment",
        isFineLine: false,
        pendingAmount: Number(e.pendingAmount || 0),
        lateWeeks: Number(e.lateWeeks || 0),
        fineAmount: 0,
        fineAmountPerWeek: FINE_AMOUNT_PER_WEEK,
      }));

      const finalFineLine =
        Number(batchFineTotal || 0) > 0
          ? [
              {
                lineNumber: pendingLineItems.length + 1,
                label: fineMode === "standard" ? "Standard Late Fine" : "Late Fine",
                description: getFinalFineLineDescription(),
                fineBreakdown:
                  fineMode === "weekly" ? weeklyFineBreakdown : [],
                amount: Number(batchFineTotal || 0),
                dueDate: "",
                status: "Pending",
                isUpcoming: false,
                lineType: "late_fine",
                isFineLine: true,
                pendingAmount: 0,
                lateWeeks:
                  fineMode === "weekly"
                    ? selected.reduce((sum, e) => sum + Number(e.lateWeeks || 0), 0)
                    : 0,
                fineAmount: Number(batchFineTotal || 0),
                fineAmountPerWeek: FINE_AMOUNT_PER_WEEK,
                fineMode,
                standardFineAmount:
                  fineMode === "standard" ? Number(standardLateFineAmount || 0) : 0,
              },
            ]
          : [];

      const lineItems = [...pendingLineItems, ...finalFineLine].map((item, index) => ({
        ...item,
        lineNumber: index + 1,
      }));

      const result = await generateBatchPendingInvoice(
        studentData,
        { ...course, feesPlan },
        lineItems
      );

      if (result.success) {
        const sentDate = new Date().toISOString().split("T")[0];
        const updatedPlan = structuredClone(feesPlan);

        selected.forEach((e) => {
          const fineForThisInstalment =
            fineMode === "weekly" ? Number(e.weeklyFineAmount || 0) : 0;

          if (e.key === "initial") {
            updatedPlan.initialDeposit = {
              ...updatedPlan.initialDeposit,
              invoiceNumber: result.invoiceNumber,
              invoiceSent: true,
              invoiceSentDate: sentDate,
              lateWeeks: e.lateWeeks,
              fineAmount: fineForThisInstalment,
              fineAmountPerWeek: FINE_AMOUNT_PER_WEEK,
              fineMode,
              fineSelected: e.fineSelected,
              fineExcluded: !e.fineSelected,
            };
          } else {
            updatedPlan.installments[e.idx] = {
              ...updatedPlan.installments[e.idx],
              invoiceNumber: result.invoiceNumber,
              invoiceSent: true,
              invoiceSentDate: sentDate,
              lateWeeks: e.lateWeeks,
              fineAmount: fineForThisInstalment,
              fineAmountPerWeek: FINE_AMOUNT_PER_WEEK,
              fineMode,
              fineSelected: e.fineSelected,
              fineExcluded: !e.fineSelected,
            };
          }
        });

        const historyEntry = {
          invoiceNumber: result.invoiceNumber,
          sentDate,
          type: "combined_with_fine",
          fineMode,
          fineAmountPerWeek: FINE_AMOUNT_PER_WEEK,
          standardFineAmount:
            fineMode === "standard" ? Number(standardLateFineAmount || 0) : 0,
          fineDescription: getFinalFineLineDescription(),
          pendingSubtotal: Number(batchPendingSubtotal || 0),
          fineAmount: Number(batchFineTotal || 0),
          totalWithFine: Number(batchPendingWithFineTotal || 0),
          items: selected.map((e) => ({
            key: e.key,
            label: e.label,
            amount: Number(e.pendingAmount || 0),
            pendingAmount: Number(e.pendingAmount || 0),
            lateWeeks: Number(e.lateWeeks || 0),
            weeklyFineAmount: Number(e.weeklyFineAmount || 0),
            fineSelected: e.fineSelected,
            fineExcluded: !e.fineSelected,
            fineMode,
            fineAmountPerWeek: FINE_AMOUNT_PER_WEEK,
            dueDate: e.installment.dueDate,
            status: e.installment.status || "Pending",
          })),
        };

        updatedPlan.invoiceHistory = [
          ...(updatedPlan.invoiceHistory || []),
          historyEntry,
        ];

        setFeesPlan(updatedPlan);
        setSendPendingDuesFineMode(false);
        setPendingDuesFineSelection([]);
        setSelectedFineKeys([]);

        await handleSave(updatedPlan);

        alert(
          `Combined invoice ${result.invoiceNumber} sent for ${selected.length} item(s)${batchFineTotal > 0 ? " with fine" : ""}!`
        );
      } else {
        alert(`Failed: ${result.error}`);
      }
    } catch (err) {
      console.error("Batch invoice with fine error:", err);
      alert(`Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };
  const handleSendCombinedReceipt = async () => {
    const selected = eligibleForReceiptBatch.filter((e) =>
      combinedReceiptSelection.includes(e.key)
    );

    if (selected.length === 0) return;

    if (!initialData.formState?.contactID) {
      alert("Student contactID is missing. Please sync student with Axcelerate first.");
      return;
    }

    const missingReceiptInfo = selected.find((e) => {
      const item = e.installment;

      return (
        !item.amountPaid ||
        item.status !== "Paid" ||
        !item.receiptReferenceDate
      );
    });

    if (missingReceiptInfo) {
      alert(
        `${missingReceiptInfo.label} is missing amount paid, paid status, or receipt reference date.`
      );
      return;
    }

    setLoading(true);

    try {
      const studentData = {
        contactID: initialData.formState.contactID,
        studentId: initialData.formState.studentId,
        firstName: initialData.formState.firstName,
        lastName: initialData.formState.lastName,
        email: initialData.formState.email,
        address: initialData.formState.onshoreAddress,
      };

      const lineItems = selected.map((e, i) => ({
        lineNumber: i + 1,
        key: e.key,
        label: e.label,
        amount: parseFloat(e.installment.amountPaid || e.installment.depositAmount || 0),
        dueDate: e.installment.dueDate,
        paidDate: e.installment.receiptReferenceDate || e.paidDate,
        invoiceNumber: e.invoiceNumber,
        status: e.installment.status || "Paid",
      }));

      const result = await generateBatchCombinedReceipt(
        studentData,
        { ...course, feesPlan },
        lineItems
      );

      if (result.success) {
        const sentDate = new Date().toISOString().split("T")[0];
        const updatedPlan = structuredClone(feesPlan);

        selected.forEach((e) => {
          if (e.key === "initial") {
            updatedPlan.initialDeposit = {
              ...updatedPlan.initialDeposit,
              receiptNumber: result.receiptNumber,
              receiptSent: true,
              receiptSentDate: sentDate,
            };
          } else {
            updatedPlan.installments[e.idx] = {
              ...updatedPlan.installments[e.idx],
              receiptNumber: result.receiptNumber,
              receiptSent: true,
              receiptSentDate: sentDate,
            };
          }
        });

        const historyEntry = {
          receiptNumber: result.receiptNumber,
          sentDate,
          type: "combined",
          items: selected.map((e) => ({
            key: e.key,
            label: e.label,
            amount: parseFloat(e.installment.amountPaid || e.installment.depositAmount || 0),
            dueDate: e.installment.dueDate,
            paidDate: e.installment.receiptReferenceDate || e.paidDate,
            invoiceNumber: e.invoiceNumber,
            status: e.installment.status || "Paid",
          })),
        };

        updatedPlan.receiptHistory = [
          ...(updatedPlan.receiptHistory || []),
          historyEntry,
        ];

        setFeesPlan(updatedPlan);
        setSendCombinedReceiptMode(false);
        setCombinedReceiptSelection([]);

        await handleSave(updatedPlan);

        alert(
          `Combined receipt ${result.receiptNumber} sent for ${selected.length} item(s)!`
        );
      } else {
        alert(`Failed: ${result.error}`);
      }
    } catch (err) {
      console.error("Combined receipt error:", err);
      alert(`Error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Modal Preview Logic
  const handlePreview = () => {
    if (!startDate) return alert("Select start date");
    const start = new Date(startDate);
    const newPreview = feesPlan.installments.map((inst, idx) => {
      const due = new Date(start);
      switch (frequency) {
        case "weekly": due.setDate(due.getDate() + 7 * idx); break;
        case "monthly": due.setMonth(due.getMonth() + idx); break;
        case "quarterly": due.setMonth(due.getMonth() + 3 * idx); break;
        default: due.setDate(due.getDate() + 30 * idx);
      }
      return { ...inst, dueDate: due.toISOString().split("T")[0] };
    });
    setPreview(newPreview);
  };
  const handleGenerateReceipt = async (installmentIdx) => {
    if (!initialData.formState?.contactID) {
      alert("Student contactID is missing. Please sync student with Axcelerate first.");
      return;
    }

    const installment = feesPlan.installments[installmentIdx];

    if (!installment.depositAmount || installment.amountPaid == "" || installment.status != "Paid") {
      alert("Please set the installment amount or amount paid before sending receipt.");
      return;
    }
    if (!installment.receiptReferenceDate || installment.amountPaid === "") {
      alert("Please set the receipt reference date shared by student or amount paid before sending receipt.");
      return;
    }

    setLoading(true);

    try {
      const studentData = {
        contactID: initialData.formState.contactID,
        studentId: initialData.formState.studentId,
        firstName: initialData.formState.firstName,
        lastName: initialData.formState.lastName,
        email: initialData.formState.email,
        address: initialData.formState.onshoreAddress,
      };

      const result = await generateInstallmentReceipt(
        studentData,
        { ...course, feesPlan },
        installmentIdx
      );

      if (result.success) {
        const updated = [...feesPlan.installments];

        updated[installmentIdx] = {
          ...updated[installmentIdx],
          receiptNumber: result.receiptNumber,
          receiptSent: true,
          receiptSentDate: new Date().toISOString().split("T")[0],
        };

        setFeesPlan((prev) => {
          const updatedInstallments = [...prev.installments];

          updatedInstallments[installmentIdx] = {
            ...updatedInstallments[installmentIdx],
            receiptNumber: result.receiptNumber,
            receiptSent: true,
            receiptSentDate: new Date().toISOString().split("T")[0],
          };

          return {
            ...prev,
            installments: updatedInstallments,
            receiptHistory: [
              ...(prev.receiptHistory || []),
              {
                receiptNumber: result.receiptNumber,
                sentDate: new Date().toISOString().split("T")[0],
                type: "single",
                items: [
                  {
                    key: `inst-${installmentIdx}`,
                    label: `Instalment #${installmentIdx + 1}`,
                    amount: parseFloat(
                      updatedInstallments[installmentIdx].amountPaid ||
                      0
                    ),
                    dueDate: updatedInstallments[installmentIdx].dueDate,
                    paidDate:
                      updatedInstallments[installmentIdx].receiptReferenceDate,
                    status: updatedInstallments[installmentIdx].status || "Paid",
                  },
                ],
              },
            ],
          };
        });

        await handleSave({
          ...feesPlan,
          installments: updated,
          receiptHistory: [
            ...(feesPlan.receiptHistory || []),
            {
              receiptNumber: result.receiptNumber,
              sentDate: new Date().toISOString().split("T")[0],
              type: "single",
              items: [
                {
                  key: `inst-${installmentIdx}`,
                  label: `Instalment #${installmentIdx + 1}`,
                  amount: parseFloat(
                    updated[installmentIdx].amountPaid ||
                    updated[installmentIdx].depositAmount ||
                    0
                  ),
                  dueDate: updated[installmentIdx].dueDate,
                  paidDate:
                    updated[installmentIdx].receiptReferenceDate ||
                    new Date().toISOString().split("T")[0],
                  status: updated[installmentIdx].status || "Paid",
                },
              ],
            },
          ],
        });

        alert(`Receipt ${result.receiptNumber} sent successfully!`);
      } else {
        alert(`Failed to send receipt: ${result.error}`);
      }
    } catch (error) {
      alert(`Error sending receipt: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateInitialInvoice = async () => {
    if (!feesPlan.initialDeposit.depositAmount || !feesPlan.initialDeposit.dueDate) {
      alert("Please set the initial deposit amount and due date before generating invoice.");
      return;
    }
    setLoading(true);
    try {
      const studentData = {
        contactID: initialData.formState.contactID,
        studentId: initialData.formState.studentId,
        firstName: initialData.formState.firstName,
        lastName: initialData.formState.lastName,
        email: initialData.formState.email,
        address: initialData.formState.onshoreAddress,
      };
      const result = await generateInitialDepositInvoice(studentData, { ...course, feesPlan });
      if (result.success) {
        const sentDate = new Date().toISOString().split("T")[0];

        const updatedPlan = {
          ...feesPlan,
          initialDeposit: {
            ...feesPlan.initialDeposit,
            invoiceNumber: result.invoiceNumber,
            invoiceSent: true,
            invoiceSentDate: sentDate,
          },
          invoiceHistory: [
            ...(feesPlan.invoiceHistory || []),
            {
              invoiceNumber: result.invoiceNumber,
              sentDate,
              type: "initial",
              items: [{
                key: "initial",
                label: "Initial Deposit",
                amount: parseFloat(feesPlan.initialDeposit.depositAmount || 0),
                dueDate: feesPlan.initialDeposit.dueDate,
                status: feesPlan.initialDeposit.status || "Pending",
              }],
            },
          ],
        };

        setFeesPlan(updatedPlan);
        await handleSave(updatedPlan);
        alert(`Invoice ${result.invoiceNumber} sent successfully!`);
      } else {
        alert(`Failed to generate invoice: ${result.error}`);
      }
    } catch (error) {
      alert(`Error generating invoice: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateInstallmentInvoice = async (installmentIdx) => {
    if (!initialData.formState?.contactID) {
      alert("Student contactID is missing. Please sync student with Axcelerate first.");
      return;
    }
    const installment = feesPlan.installments[installmentIdx];
    if (!installment.depositAmount || !installment.dueDate) {
      alert("Please set the installment amount and due date before generating invoice.");
      return;
    }
    setLoading(true);
    try {
      const studentData = {
        contactID: initialData.formState.contactID,
        studentId: initialData.formState.studentId,
        firstName: initialData.formState.firstName,
        lastName: initialData.formState.lastName,
        email: initialData.formState.email,
        address: initialData.formState.onshoreAddress,
      };
      const result = await generateInstallmentInvoice(studentData, { ...course, feesPlan }, installmentIdx);
      if (result.success) {
        const updated = [...feesPlan.installments];
        updated[installmentIdx] = {
          ...updated[installmentIdx],
          invoiceNumber: result.invoiceNumber,
          invoiceSent: true,
          invoiceSentDate: new Date().toISOString().split("T")[0],
        };
        setFeesPlan(prev => {
          const updated = [...prev.installments];
          updated[installmentIdx] = {
            ...updated[installmentIdx],
            invoiceNumber: result.invoiceNumber,
            invoiceSent: true,
            invoiceSentDate: new Date().toISOString().split("T")[0],
          };

          return {
            ...prev,
            installments: updated,
            invoiceHistory: [
              ...(prev.invoiceHistory || []),
              {
                invoiceNumber: result.invoiceNumber,
                sentDate: new Date().toISOString().split("T")[0],
                type: "single",
                items: [
                  {
                    key: `inst-${installmentIdx}`,
                    label: `Instalment #${installmentIdx + 1}`,
                    amount: parseFloat(updated[installmentIdx].depositAmount || 0),
                    dueDate: updated[installmentIdx].dueDate,
                    status: updated[installmentIdx].status || "Pending",
                  },
                ],
              },
            ],
          };
        });
        await handleSave({
          ...feesPlan,
          installments: updated,
          invoiceHistory: [
            ...(feesPlan.invoiceHistory || []),
            {
              invoiceNumber: result.invoiceNumber,
              sentDate: new Date().toISOString().split("T")[0],
              type: "single",
              items: [{
                key: `inst-${installmentIdx}`,
                label: `Instalment #${installmentIdx + 1}`,
                amount: parseFloat(updated[installmentIdx].depositAmount || 0),
                dueDate: updated[installmentIdx].dueDate,
                status: updated[installmentIdx].status || "Pending",
              }],
            },
          ],
        });
        alert(`Invoice ${result.invoiceNumber} sent successfully!`);
      } else {
        alert(`Failed to generate invoice: ${result.error}`);
      }
    } catch (error) {
      alert(`Error generating invoice: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const applyPreview = () => {
    setFeesPlan((prev) => ({ ...prev, installments: preview }));
    setModalOpen(false);
  };

  useEffect(() => {
    const enrollment = parseFloat(feesPlan.enrollmentFees || 0);
    const material = parseFloat(feesPlan.materialFees || 0);
    const tuition = parseFloat(feesPlan.tuitionFees || 0);
    const additionalFees = parseFloat(feesPlan.additionalFees || 0);
    const oshcFees = parseFloat(feesPlan.oshcFees || 0);
    const totalFees = (enrollment + material + tuition + additionalFees + oshcFees).toFixed(2);
    const initialAmount = parseFloat(feesPlan.initialDeposit.depositAmount || 0);
    const remaining = totalFees - initialAmount;
    const numInstallments = parseInt(feesPlan.numOfInstallments || 1);
    const perInstallment = numInstallments > 0 ? (remaining / numInstallments).toFixed(2) : 0;

    const newInstallments = Array.from({ length: numInstallments }, (_, idx) => {
      const existing = feesPlan.installments[idx] || {};
      const isEdited = feesPlan.editedIndexes.includes(idx);
      return {
        ...existing,
        id: existing.id || uuidv4(),
        depositAmount: isEdited && feesPlan.customInstallments ? existing.depositAmount : perInstallment,
        dueDate: existing.dueDate || "",
        status: existing.status || "",
        amountPaid: existing.amountPaid || "",
        commissionDue: existing.commissionDue || "",
        commissionPaid: existing.commissionPaid || "",
      };
    });

    if (!feesPlan.customInstallments) {
      setFeesPlan((prev) => ({ ...prev, totalFees, installments: newInstallments }));
    } else {
      setFeesPlan((prev) => ({ ...prev, totalFees }));
    }
  }, [
    feesPlan.enrollmentFees, feesPlan.materialFees, feesPlan.tuitionFees,
    feesPlan.additionalFees, feesPlan.oshcFees, feesPlan.initialDeposit.depositAmount,
    feesPlan.numOfInstallments, feesPlan.customInstallments,
  ]);

  useEffect(() => {
    const fetchAgentCommission = async () => {
      if (initialData?.formState?.agentName) {
        try {
          const agent = await getAgentByName(initialData?.formState?.agentName);
          if (agent) setFeesPlan((prev) => ({ ...prev, commissionRate: agent.commissionRate || 0 }));
        } catch (err) {
          console.error("Failed to fetch agent commission:", err);
        }
      }
    };
    fetchAgentCommission();
  }, [feesPlan.commissionRate]);

  useEffect(() => {
    if (course.feesPlan) return;
    const fetchCourseDetails = async () => {
      if (course?.courseName) {
        try {
          const coursebyName = await getFirstCourseByName(course?.courseName);
          if (coursebyName && initialData.formState?.visaApplicationType !== "Onshore") {
            setFeesPlan((prev) => ({
              ...prev,
              enrollmentFees: coursebyName.offshoreEnrolmentFee || 0,
              materialFees: coursebyName.offshoreMaterialFee || 0,
              tuitionFees: coursebyName.offshoreTuitionFee || 0,
            }));
          } else if (coursebyName) {
            setFeesPlan((prev) => ({
              ...prev,
              enrollmentFees: coursebyName.onshoreEnrolmentFee || 0,
              materialFees: coursebyName.onshoreMaterialFee || 0,
              tuitionFees: coursebyName.onshoreTuitionFee || 0,
            }));
          }
        } catch (err) {
          console.error("Failed to fetch course details:", err);
        }
      }
    };
    fetchCourseDetails();
  }, [feesPlan.enrollmentFees, feesPlan.materialFees, feesPlan.tuitionFees]);

  const handleChange = (field, value) =>
    setFeesPlan((prev) => ({ ...prev, [field]: value }));

  const handleInitialDepositChange = async (field, value) => {
    let updatedInitial = { ...feesPlan.initialDeposit, [field]: value };
    const depositAmount = parseFloat(updatedInitial.depositAmount || 0);
    const amountPaid = parseFloat(updatedInitial.amountPaid || 0);
    const dueDate = updatedInitial.dueDate ? new Date(updatedInitial.dueDate) : null;
    const today = new Date();

    if (depositAmount > 0 && amountPaid >= depositAmount) {
      updatedInitial.status = "Paid";
    } else if (dueDate && today > dueDate && amountPaid < depositAmount) {
      updatedInitial.status = "Overdue";
    } else {
      updatedInitial.status = "Pending";
    }

    if (field === "receiptDoc" && value) {
      try {
        const uploadedFiles = await fileStorageService.uploadFile(
          value,
          `receipt/${initialData.formState?.studentId}/${course.coeNumber}/InitialDeposit`
        );
        updatedInitial[field] = { url: uploadedFiles.url, path: uploadedFiles.path, name: value.name };
      } catch (err) {
        console.error("File upload failed:", err);
      }
    }
    setFeesPlan(prev => ({ ...prev, initialDeposit: updatedInitial }));
  };

  const handleInstallmentChange = async (idx, field, value) => {
    const updated = [...feesPlan.installments];
    updated[idx][field] = value;

    if (field === "receiptDoc" && value) {
      try {
        const uploadedFiles = await fileStorageService.uploadFile(
          value,
          `receipt/${initialData.formState?.studentId}/${course.coeNumber}/Installment${idx}`
        );
        updated[idx][field] = { url: uploadedFiles.url, path: uploadedFiles.path, name: value.name };
      } catch (err) {
        console.error("File upload failed:", err);
        return;
      }
    }

    const depositAmount = parseFloat(updated[idx].depositAmount || 0);
    const amountPaid = parseFloat(updated[idx].amountPaid || 0);
    const dueDate = updated[idx].dueDate ? new Date(updated[idx].dueDate) : null;
    const today = new Date();

    if (amountPaid >= depositAmount && depositAmount > 0) {
      updated[idx].status = "Paid";
    } else if (amountPaid === 0 && dueDate && today < dueDate) {
      updated[idx].status = "Pending";
    } else if ((amountPaid < depositAmount && dueDate && today > dueDate) || (dueDate && today > dueDate)) {
      updated[idx].status = "Overdue";
    } else {
      updated[idx].status = "Pending";
    }

    setFeesPlan((prev) => ({
      ...prev,
      installments: updated,
      editedIndexes: [...new Set([...prev.editedIndexes, idx])],
    }));
  };

  const isOnOrBeforeToday = (dateStr) => {
    if (!dateStr) return false;
    const d = new Date(dateStr); const today = new Date();
    today.setHours(0, 0, 0, 0); d.setHours(0, 0, 0, 0);
    return d <= today;
  };

  const totalCommission = (
    parseFloat(calculateCommission(feesPlan.initialDeposit.depositAmount, feesPlan.commissionRate)) +
    feesPlan.installments.reduce((sum, inst) => sum + parseFloat(calculateCommission(inst.depositAmount, feesPlan.commissionRate) || 0), 0)
  ).toFixed(2);

  const totalCommissionDue = (
    parseFloat(calculateCommission(feesPlan.initialDeposit.amountPaid, feesPlan.commissionRate)) +
    feesPlan.installments.reduce((sum, inst) => sum + parseFloat(calculateCommission(inst.amountPaid, feesPlan.commissionRate) || 0), 0)
  ).toFixed(2);

  const totalPaid = (
    parseFloat(feesPlan.initialDeposit.amountPaid || 0) +
    feesPlan.installments.reduce((sum, inst) => sum + parseFloat(inst.amountPaid || 0), 0)
  ).toFixed(2);

  const dueTillToday = (
    (isOnOrBeforeToday(feesPlan.initialDeposit.dueDate) ? parseFloat(feesPlan.initialDeposit.depositAmount || 0) : 0) +
    feesPlan.installments.reduce((sum, inst) => isOnOrBeforeToday(inst.dueDate) ? sum + parseFloat(inst.depositAmount || 0) : sum, 0)
  ).toFixed(2);

  const paidTillToday = (
    (isOnOrBeforeToday(feesPlan.initialDeposit.dueDate) ? parseFloat(feesPlan.initialDeposit.amountPaid || 0) : 0) +
    feesPlan.installments.reduce((sum, inst) => isOnOrBeforeToday(inst.dueDate) ? sum + parseFloat(inst.amountPaid || 0) : sum, 0)
  ).toFixed(2);

  const totalDue = Math.max(dueTillToday - paidTillToday, 0).toFixed(2);

  const nextDueDate = [...feesPlan.installments]
    .filter((inst) => inst.status !== "Paid" && inst.dueDate)
    .sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate))[0]?.dueDate || "-";

  const totalInstallmentsAmount = feesPlan.installments.reduce((sum, inst) => sum + parseFloat(inst.depositAmount || 0), 0);
  const totalPlanned = parseFloat(feesPlan.initialDeposit.depositAmount || 0) + totalInstallmentsAmount;
  const amountMismatch = Math.abs(totalPlanned - feesPlan.totalFees) > 1;

  const handleSave = async (planToSave = feesPlan) => {
    setSaving(true);
    setSaveMessage("");
    setLoading(true);

    try {
      const errors = [];

      const initialStatus = String(planToSave.initialDeposit?.status || "").toLowerCase();

      if (
        initialStatus === "paid" &&
        (!planToSave.initialDeposit?.receiptDoc ||
          planToSave.initialDeposit.receiptDoc.length === 0)
      ) {
        errors.push("Initial Deposit receipt is missing.");
      }

      (planToSave.installments || []).forEach((inst, idx) => {
        const status = String(inst?.status || "").toLowerCase();

        if (
          status === "paid" &&
          (!inst.receiptDoc || inst.receiptDoc.length === 0)
        ) {
          errors.push(`Installment #${idx + 1} receipt is missing.`);
        }
      });

      const totalInstallmentsAmount = (planToSave.installments || []).reduce(
        (sum, inst) => sum + parseFloat(inst.depositAmount || 0),
        0
      );

      const totalPlanned =
        parseFloat(planToSave.initialDeposit?.depositAmount || 0) +
        totalInstallmentsAmount;

      if (Math.abs(totalPlanned - parseFloat(planToSave.totalFees || 0)) > 1) {
        errors.push("Total of installments does not match total fees.");
      }

      if (errors.length > 0) {
        const errorMessage = errors.join(" ");
        setSaveMessage(errorMessage);
        alert("Cannot save: " + errorMessage);
        return;
      }

      let targetListKey =
        initialData.formState?.feePlanType === "Package" ? "packages" : "courses";

      const formStateCopy = structuredClone(initialData.formState || {});

      if (!formStateCopy[targetListKey]) formStateCopy[targetListKey] = [];

      const list = formStateCopy[targetListKey];

      const index = list.findIndex(
        (c) =>
          (c.courseName === course?.courseName && targetListKey === "courses") ||
          (c.name === course?.name && targetListKey === "packages")
      );

      const newFeesPlan = structuredClone(planToSave);

      if (index >= 0) {
        list[index] = {
          ...list[index],
          feesPlan: newFeesPlan,
        };
      } else {
        const newEntry =
          targetListKey === "courses"
            ? {
                courseName: course.courseName || "unknownCourse",
                feesPlan: newFeesPlan,
              }
            : {
                name: course.name || `Package ${list.length + 1}`,
                feesPlan: newFeesPlan,
              };

        list.push(newEntry);
      }

      const updatedFormState = {
        ...formStateCopy,
        [targetListKey]: list,
      };

      course.feesPlan = newFeesPlan;

      if (initialData?.id) {
        await updateStudentFeesPlan(
          initialData.id,
          updatedFormState,
          initialData.enrolmentReferenceId
        );
      }

      setSaveMessage("Fees plan saved successfully!");
      alert("Fees plan saved successfully!");
    } catch (error) {
      console.error("Failed to save fees plan:", error);
      setSaveMessage("Failed to save fees plan. Please try again.");
      alert("Failed to save fees plan. Please try again.");
    } finally {
      setSaving(false);
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: 24 }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 24 }}>
        <div>
          <h1>Fees Plan - {initialData.formState?.feePlanType === "Package"
            ? "Package " + (course?.name || "")
            : course?.courseName || ""}</h1>
          <p>Manage fees, installments, follow-ups & tracking</p>
        </div>
        <div style={{ display: "flex", gap: 12, alignItems: "start" }}>
          <Button variant="secondary" onClick={onCancel}>Cancel</Button>
          <Button onClick={() => handleSave()} loading={saving}>Save</Button>
        </div>
      </div>

      {loading && (
        <div style={{ position: "absolute", inset: 0, background: "rgba(255,255,255,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 10, borderRadius: "12px" }}>
          <div className="spinner" style={{ textAlign: "center" }}>
            <div className="loader" style={{ width: 48, height: 48, borderRadius: "50%", animation: "spin 1s linear infinite", margin: "0 auto 12px" }} />
          </div>
          <p style={{ padding: "0.5rem" }}>Processing, please wait!</p>
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: "flex", gap: 12, marginBottom: 24, borderBottom: "2px solid #e5e7eb", paddingBottom: 8 }}>
        {[
          { key: "summary", label: "Summary" },
          { key: "feePlan", label: "Fee Plan" },
          { key: "initial", label: "Initial Installment" },
          { key: "installments", label: "Installments" },
          { key: "invoiceHistory", label: "Invoice History" },
          { key: "receiptHistory", label: "Receipt History" },
        ].map((tab) => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)} style={{
            padding: "8px 16px", borderRadius: 8,
            background: activeTab === tab.key ? "#eef2ff" : "transparent",
            border: "none", cursor: "pointer",
            fontWeight: activeTab === tab.key ? 600 : 400,
          }}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Summary Tab */}
      {activeTab === "summary" && (
        <div>
          <SectionTitle icon={BarChart2}>Plan Summary</SectionTitle>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16, background: "#f9fafb", padding: 16, borderRadius: 12 }}>
            <div><strong>Total Fees:</strong> ${feesPlan.totalFees}</div>
            <div><strong>Total Paid:</strong> ${totalPaid}</div>
            <div><strong>Amount Due:</strong> ${totalDue}</div>
            <div><strong>Total Commission:</strong> ${totalCommission}</div>
            <div><strong>Commission Due (Paid):</strong> ${totalCommissionDue}</div>
            <div><strong>Commission Remaining:</strong> ${(totalCommission - totalCommissionDue).toFixed(2)}</div>
            <div><strong>Next Due Date:</strong> {nextDueDate}</div>
          </div>
          <SectionTitle icon={Calendar}>Installments Overview</SectionTitle>
          <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 12 }}>
            <thead>
              <tr style={{ background: "#eef2ff", textAlign: "left" }}>
                <th style={{ padding: 8 }}>#</th>
                <th style={{ padding: 8 }}>Due Date</th>
                <th style={{ padding: 8 }}>Amount</th>
                <th style={{ padding: 8 }}>Paid</th>
                <th style={{ padding: 8 }}>Commission</th>
                <th style={{ padding: 8 }}>Commission Due</th>
                <th style={{ padding: 8 }}>Status</th>
                <th style={{ padding: 8 }}>Invoice</th>
              </tr>
            </thead>
            <tbody>
              <tr style={{ borderBottom: "1px solid #e5e7eb" }}>
                <td style={{ padding: 8 }}>Initial</td>
                <td style={{ padding: 8 }}>{feesPlan.initialDeposit.dueDate || "-"}</td>
                <td style={{ padding: 8 }}>${feesPlan.initialDeposit.depositAmount || 0}</td>
                <td style={{ padding: 8 }}>${feesPlan.initialDeposit.amountPaid || 0}</td>
                <td style={{ padding: 8 }}>${calculateCommission(feesPlan.initialDeposit.depositAmount, feesPlan.commissionRate)}</td>
                <td style={{ padding: 8 }}>${calculateCommission(feesPlan.initialDeposit.amountPaid, feesPlan.commissionRate)}</td>
                <td style={{ padding: 8 }}><StatusBadge status={feesPlan.initialDeposit.status || "Pending"} /></td>
                <td style={{ padding: 8 }}>
                  <InvoiceSentIndicator installment={feesPlan.initialDeposit} />
                </td>
              </tr>
              {feesPlan.installments.map((inst, idx) => (
                <tr key={inst.id} style={{ borderBottom: "1px solid #e5e7eb" }}>
                  <td style={{ padding: 8 }}>{idx + 1}</td>
                  <td style={{ padding: 8 }}>{inst.dueDate || "-"}</td>
                  <td style={{ padding: 8 }}>${inst.depositAmount || 0}</td>
                  <td style={{ padding: 8 }}>${inst.amountPaid || 0}</td>
                  <td style={{ padding: 8 }}>${calculateCommission(inst.depositAmount, feesPlan.commissionRate)}</td>
                  <td style={{ padding: 8 }}>${calculateCommission(inst.amountPaid, feesPlan.commissionRate)}</td>
                  <td style={{ padding: 8 }}><StatusBadge status={inst.status || "Pending"} /></td>
                  <td style={{ padding: 8 }}>
                    <InvoiceSentIndicator installment={inst} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Fee Plan Tab */}
      {activeTab === "feePlan" && (
        <div>
          <SectionTitle icon={DollarSign}>Fee Plan</SectionTitle>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12 }}>
            {["commissionRate", "enrollmentFees", "materialFees", "tuitionFees", "additionalFees", "oshcFees", "numOfInstallments"].map((field) => (
              <FormField key={field} label={field.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase())}>
                <Input
                  type={field === "numOfInstallments" ? "number" : "text"}
                  value={feesPlan[field]}
                  onChange={(e) => handleChange(field, e.target.value)}
                  disabled={feesPlan.customInstallments && field === "numOfInstallments"}
                />
              </FormField>
            ))}
            <FormField label="Total Fees">
              <Input type="number" value={feesPlan.totalFees} disabled />
            </FormField>
            <FormField label="Custom Installments?">
              <Select value={feesPlan.customInstallments ? "yes" : "no"}
                onChange={(e) => setFeesPlan((prev) => ({ ...prev, customInstallments: e.target.value === "yes" }))}>
                <option value="no">No (Auto-managed)</option>
                <option value="yes">Yes (Manual)</option>
              </Select>
            </FormField>
          </div>
        </div>
      )}

      {/* Initial Installment Tab */}
      {activeTab === "initial" && (
        <div>
          <SectionTitle>Initial Installment</SectionTitle>
          <InstallmentCard
            idx={-1}
            installment={feesPlan.initialDeposit}
            commissionRate={feesPlan.commissionRate}
            onChange={(idx, field, val) => handleInitialDepositChange(field, val)}
            onGenerateInvoice={handleGenerateInitialInvoice}
            onGenerateReceipt={handleGenerateReceipt}
            label="Initial Deposit"
            alwaysExpanded={true}
            customInstallments={true}
          />
        </div>
      )}

      {/* Installments Tab */}
      {activeTab === "installments" && (
        <div>
          {/* Action bar */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 12 }}>
            <SectionTitle>Installments</SectionTitle>
            <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
              {!feesPlan.customInstallments && (
                <Button size="sm" onClick={() => setModalOpen(true)}>Calculate Installment Dates</Button>
              )}
              {feesPlan.customInstallments && (
                <Button size="sm" onClick={() => {
                  setFeesPlan((prev) => ({
                    ...prev,
                    installments: [...prev.installments, { id: uuidv4(), depositAmount: "", dueDate: "", status: "Pending", amountPaid: "", commissionDue: "", commissionPaid: "" }],
                    numOfInstallments: prev.installments.length + 1,
                  }));
                }}>+ Add Installment</Button>
              )}

              {/* Send Pending Dues toggle */}
              <Button
                size="sm"
                onClick={() => {
                  setSendPendingDuesMode((prev) => !prev);
                  setPendingDuesSelection([]);
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  background: sendPendingDuesMode ? "rgba(5, 110, 196, 0.1)" : undefined, // light primary
                  border: sendPendingDuesMode ? "1px solid var(--color-primary)" : undefined,
                  color: sendPendingDuesMode ? "var(--color-secondary)" : undefined,
                }}
              >
                <span style={{ fontSize: 14 }}>
                  {sendPendingDuesMode ? "☑" : "☐"}
                </span>
                <span>Send Pending Dues</span>
              </Button>
              {/* Send Combined Receipt toggle */}
              <Button
                size="sm"
                onClick={() => {
                  setSendCombinedReceiptMode((prev) => !prev);
                  setCombinedReceiptSelection([]);
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  background: sendCombinedReceiptMode ? "rgba(5, 110, 196, 0.1)" : undefined, // light primary
                  border: sendCombinedReceiptMode ? "1px solid var(--color-primary)" : undefined,
                  color: sendCombinedReceiptMode ? "var(--color-secondary)" : undefined,
                }}
              >
                <span style={{ fontSize: 14 }}>
                  {sendCombinedReceiptMode ? "☑" : "☐"}
                </span>
                <span>Send Combined Receipt</span>
              </Button>
              {/* Send Pending Dues With Fine toggle */}
              <Button
                size="sm"
                onClick={() => {
                  setSendPendingDuesFineMode((prev) => !prev);
                  setPendingDuesFineSelection([]);
                  setSelectedFineKeys([]);

                  // Close other panels
                  setSendPendingDuesMode(false);
                  setSendCombinedReceiptMode(false);
                  setCombinedReceiptSelection([]);
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  background: sendPendingDuesFineMode ? "#faf7f7" : undefined,
                  border: sendPendingDuesFineMode ? "1px solid #d8b4b4" : undefined,
                  color: sendPendingDuesFineMode ? "#9f3a38" : undefined,
                }}
              >
                <span style={{ fontSize: 14 }}>
                  {sendPendingDuesFineMode ? "☑" : "☐"}
                </span>
                <span>Send Pending Dues With Fine</span>
              </Button>
            </div>
          </div>

          {/* Mismatch warning */}
          {amountMismatch && (
            <div style={{ background: "#fee2e2", color: "#991b1b", padding: "12px 16px", borderRadius: 8, marginBottom: 12, fontWeight: 500 }}>
              ⚠️ The total of your installments doesn't match the total fees.
            </div>
          )}

          {/* ── Pending Dues Panel (inline, above cards) ── */}
          {sendPendingDuesMode && (
            <div style={{ border: "1px solid #c7d2fe", borderRadius: 10, background: "#f5f3ff", marginBottom: 20, overflow: "hidden" }}>
              {/* Panel header */}
              <div style={{ background: "#eef2ff", padding: "10px 16px", borderBottom: "1px solid #c7d2fe", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ fontWeight: 600, fontSize: 14, color: "#3730a3" }}>
                  <Mail style={{ width: 15, height: 15, marginRight: 6, verticalAlign: "middle" }} />
                  Select instalments for combined invoice
                </div>
                <div style={{ fontSize: 12, color: "#6b7280" }}>
                  All selected will appear as line items on <strong>one PDF invoice</strong> sent via Axcelerate
                </div>
              </div>

              {eligibleForBatch.length === 0 ? (
                <div style={{ padding: "20px 16px", color: "#6b7280", fontSize: 13, textAlign: "center" }}>
                  No overdue or pending instalments found.
                </div>
              ) : (
                <div style={{ padding: "0 16px 16px" }}>
                  {/* Column headers */}
                  <div style={{ display: "grid", gridTemplateColumns: "32px 1fr 110px 100px 90px 90px", gap: 8, padding: "10px 0 6px", borderBottom: "1px solid #ddd6fe", marginBottom: 4 }}>
                    <input
                      type="checkbox"
                      style={{ width: 15, height: 15, accentColor: "var(--color-primary)", marginTop: 2 }}
                      checked={pendingDuesSelection.length === eligibleForBatch.length && eligibleForBatch.length > 0}
                      onChange={e => setPendingDuesSelection(e.target.checked ? eligibleForBatch.map(x => x.key) : [])}
                    />
                    <span style={{ fontSize: 11, fontWeight: 600, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.05em" }}>Instalment</span>
                    <span style={{ fontSize: 11, fontWeight: 600, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.05em" }}>Due Date</span>
                    <span style={{ fontSize: 11, fontWeight: 600, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.05em", textAlign: "right" }}>Amount</span>
                    <span style={{ fontSize: 11, fontWeight: 600, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.05em" }}>Status</span>
                    <span style={{ fontSize: 11, fontWeight: 600, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.05em" }}>Invoice</span>
                  </div>

                  {/* Each eligible instalment as a row */}
                  {eligibleForBatch.map((item, rowIdx) => {
                    const isSelected = pendingDuesSelection.includes(item.key);
                    return (
                      <div
                        key={item.key}
                        onClick={() => setPendingDuesSelection(prev =>
                          prev.includes(item.key) ? prev.filter(k => k !== item.key) : [...prev, item.key]
                        )}
                        style={{
                          display: "grid",
                          gridTemplateColumns: "32px 1fr 110px 100px 90px 90px",
                          gap: 8,
                          padding: "9px 0",
                          borderBottom: rowIdx < eligibleForBatch.length - 1 ? "1px solid #ede9fe" : "none",
                          alignItems: "center",
                          cursor: "pointer",
                          background: isSelected ? "#ede9fe" : "transparent",
                          borderRadius: 6,
                          marginBottom: 2,
                          paddingLeft: 4,
                          paddingRight: 4,
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => {}}
                          style={{ width: 15, height: 15, accentColor: "#4f46e5" }}
                        />
                        <span style={{ fontSize: 13, fontWeight: 500 }}>
                          {item.label}
                          {item.isUpcoming && (
                            <span style={{ marginLeft: 6, fontSize: 11, background: "#dbeafe", color: "#1e40af", padding: "1px 7px", borderRadius: 20 }}>
                              next upcoming
                            </span>
                          )}
                        </span>
                        <span style={{ fontSize: 13, color: "#374151" }}>{item.installment.dueDate || "-"}</span>
                        <span style={{ fontSize: 13, fontWeight: 600, textAlign: "right", color: "#111827" }}>
                          ${parseFloat(item.installment.depositAmount || 0).toFixed(2)}
                        </span>
                        <StatusBadge status={item.installment.status || "Pending"} />
                        <span style={{ fontSize: 12, color: item.installment.invoiceSent ? "#059669" : "#9ca3af" }}>
                          {item.installment.invoiceSent
                            ? <><CheckCircle style={{ width: 13, height: 13, marginRight: 3, verticalAlign: "middle" }} />Sent</>
                            : "Not sent"}
                        </span>
                      </div>
                    );
                  })}

                  {/* Total + payment plan summary */}
                  {pendingDuesSelection.length > 0 && (
                    <div style={{ marginTop: 14, padding: "12px 14px", background: "#fff", border: "1px solid #c7d2fe", borderRadius: 8 }}>
                      <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 8, fontWeight: 500 }}>
                        Invoice preview — {pendingDuesSelection.length} payment plan line item{pendingDuesSelection.length > 1 ? "s" : ""}:
                      </div>
                      {/* Line items preview (mirrors what goes into the PDF) */}
                      {eligibleForBatch
                        .filter(e => pendingDuesSelection.includes(e.key))
                        .map((e, i) => (
                          <div key={e.key} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, padding: "4px 0", borderBottom: "1px dashed #e5e7eb" }}>
                            <span style={{ color: "#374151" }}>
                              {i + 1}. {e.label} — due {e.installment.dueDate}
                              {e.isUpcoming ? " (upcoming)" : ""}
                            </span>
                            <span style={{ fontWeight: 600 }}>${parseFloat(e.installment.depositAmount || 0).toFixed(2)}</span>
                          </div>
                        ))}
                      {/* Total row */}
                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14, fontWeight: 700, marginTop: 8, paddingTop: 6, borderTop: "2px solid #818cf8", color: "#3730a3" }}>
                        <span>Total invoice amount</span>
                        <span>${batchTotal.toFixed(2)}</span>
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 14 }}>
                    <Button variant="secondary" size="sm" onClick={() => { setSendPendingDuesMode(false); setPendingDuesSelection([]); }}>
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      disabled={pendingDuesSelection.length === 0 || loading}
                      loading={loading}
                      onClick={handleSendPendingDuesInvoice}
                    >
                      <Mail style={{ width: 14, height: 14, marginRight: 5 }} />
                      Send combined invoice ({pendingDuesSelection.length})
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
          {/* ── Combined Receipt Panel (inline, above cards) ── */}
          {sendCombinedReceiptMode && (
            <div style={{ border: "1px solid #c7d2fe", borderRadius: 10, background: "#f5f3ff", marginBottom: 20, overflow: "hidden" }}>
              {/* Panel header */}
              <div style={{ background: "#eef2ff", padding: "10px 16px", borderBottom: "1px solid #c7d2fe", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ fontWeight: 600, fontSize: 14, color: "#3730a3" }}>
                  <Mail style={{ width: 15, height: 15, marginRight: 6, verticalAlign: "middle" }} />
                  Select instalments for combined Receipt
                </div>
                <div style={{ fontSize: 12, color: "#6b7280" }}>
                  All selected will appear as line items on <strong>one PDF receipt</strong> sent via Axcelerate
                </div>
              </div>

              {eligibleForReceiptBatch.length === 0 ? (
                <div style={{ padding: "20px 16px", color: "#6b7280", fontSize: 13, textAlign: "center" }}>
                  No overdue or pending instalments found.
                </div>
              ) : (
                <div style={{ padding: "0 16px 16px" }}>
                  {/* Column headers */}
                  <div style={{ display: "grid", gridTemplateColumns: "32px 1fr 110px 100px 90px 90px", gap: 8, padding: "10px 0 6px", borderBottom: "1px solid #ddd6fe", marginBottom: 4 }}>
                    <input
                      type="checkbox"
                      style={{ width: 15, height: 15, accentColor: "var(--color-primary)", marginTop: 2 }}
                      checked={combinedReceiptSelection.length === eligibleForReceiptBatch.length && eligibleForReceiptBatch.length > 0}
                      onChange={e => setCombinedReceiptSelection(e.target.checked ? eligibleForReceiptBatch.map(x => x.key) : [])}
                    />
                    <span style={{ fontSize: 11, fontWeight: 600, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.05em" }}>Instalment</span>
                    <span style={{ fontSize: 11, fontWeight: 600, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.05em" }}>Paid on</span>
                    <span style={{ fontSize: 11, fontWeight: 600, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.05em", textAlign: "right" }}>Amount</span>
                    <span style={{ fontSize: 11, fontWeight: 600, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.05em" }}>Status</span>
                    <span style={{ fontSize: 11, fontWeight: 600, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.05em" }}>Receipt</span>
                  </div>

                  {/* Each eligible instalment as a row */}
                  {eligibleForReceiptBatch.map((item, rowIdx) => {
                    const isSelected = combinedReceiptSelection.includes(item.key);
                    return (
                      <div
                        key={item.key}
                        onClick={() => setCombinedReceiptSelection(prev =>
                          prev.includes(item.key) ? prev.filter(k => k !== item.key) : [...prev, item.key]
                        )}
                        style={{
                          display: "grid",
                          gridTemplateColumns: "32px 1fr 110px 100px 90px 90px",
                          gap: 8,
                          padding: "9px 0",
                          borderBottom: rowIdx < eligibleForReceiptBatch.length - 1 ? "1px solid #ede9fe" : "none",
                          alignItems: "center",
                          cursor: "pointer",
                          background: isSelected ? "#ede9fe" : "transparent",
                          borderRadius: 6,
                          marginBottom: 2,
                          paddingLeft: 4,
                          paddingRight: 4,
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => {}}
                          style={{ width: 15, height: 15, accentColor: "#4f46e5" }}
                        />
                        <span style={{ fontSize: 13, fontWeight: 500 }}>
                          {item.label}
                          {item.isUpcoming && (
                            <span style={{ marginLeft: 6, fontSize: 11, background: "#dbeafe", color: "#1e40af", padding: "1px 7px", borderRadius: 20 }}>
                              next upcoming
                            </span>
                          )}
                        </span>
                        <span style={{ fontSize: 13, color: "#374151" }}>{item.installment.receiptReferenceDate || "-"}</span>
                        <span style={{ fontSize: 13, fontWeight: 600, textAlign: "right", color: "#111827" }}>
                          ${parseFloat(item.installment.depositAmount || 0).toFixed(2)}
                        </span>
                        <StatusBadge status={item.installment.status || "Pending"} />
                        <span style={{ fontSize: 12, color: item.installment.invoiceSent ? "#059669" : "#9ca3af" }}>
                          {item.installment.invoiceSent
                            ? <><CheckCircle style={{ width: 13, height: 13, marginRight: 3, verticalAlign: "middle" }} />Sent</>
                            : "Not sent"}
                        </span>
                      </div>
                    );
                  })}

                  {/* Total + payment plan summary */}
                  {combinedReceiptSelection.length > 0 && (
                    <div style={{ marginTop: 14, padding: "12px 14px", background: "#fff", border: "1px solid #c7d2fe", borderRadius: 8 }}>
                      <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 8, fontWeight: 500 }}>
                        Receipt preview — {combinedReceiptSelection.length} payment plan line item{combinedReceiptSelection.length > 1 ? "s" : ""}:
                      </div>
                      {/* Line items preview (mirrors what goes into the PDF) */}
                      {eligibleForReceiptBatch
                        .filter(e => combinedReceiptSelection.includes(e.key))
                        .map((e, i) => (
                          <div key={e.key} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, padding: "4px 0", borderBottom: "1px dashed #e5e7eb" }}>
                            <span style={{ color: "#374151" }}>
                              {i + 1}. {e.label} — due {e.installment.dueDate}
                              {e.isUpcoming ? " (upcoming)" : ""}
                            </span>
                            <span style={{ fontWeight: 600 }}>${parseFloat(e.installment.depositAmount || 0).toFixed(2)}</span>
                          </div>
                        ))}
                      {/* Total row */}
                      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14, fontWeight: 700, marginTop: 8, paddingTop: 6, borderTop: "2px solid #818cf8", color: "#3730a3" }}>
                        <span>Total invoice amount</span>
                        <span>${batchTotal.toFixed(2)}</span>
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 14 }}>
                    <Button variant="secondary" size="sm" onClick={() => { setSendCombinedReceiptMode(false); setCombinedReceiptSelection([]); }}>
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      disabled={combinedReceiptSelection.length === 0 || loading}
                      loading={loading}
                      onClick={handleSendCombinedReceipt}
                    >
                      <Mail style={{ width: 14, height: 14, marginRight: 5 }} />
                      Send combined receipt ({combinedReceiptSelection.length})
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
          {/* ── Pending Dues With Fine Panel ── */}
          {sendPendingDuesFineMode && (
            <div
              style={{
                border: "1px solid #f3d6d6",
                borderRadius: 10,
                background: "#fffdfc",
                marginBottom: 20,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  background: "#faf7f7",
                  padding: "10px 16px",
                  borderBottom: "1px solid #f3d6d6",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 12,
                }}
              >
                <div style={{ fontWeight: 600, fontSize: 14, color: "#7a3b3b" }}>
                  <Mail
                    style={{
                      width: 15,
                      height: 15,
                      marginRight: 6,
                      verticalAlign: "middle",
                    }}
                  />
                  Select instalments for invoice with optional fine
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 12, color: "#6b7280", fontWeight: 600 }}>
                    Fine option
                  </span>

                  <Select
                    value={fineMode}
                    onChange={(e) => setFineMode(e.target.value)}
                    style={{ width: 190, fontSize: 12 }}
                  >
                    <option value="weekly">Auto weekly fine</option>
                    <option value="standard">One standard fine</option>
                  </Select>

                  {fineMode === "weekly" && (
                    <span style={{ fontSize: 12, color: "#7a3b3b", fontWeight: 600 }}>
                      ${FINE_AMOUNT_PER_WEEK} per late week, added as one final fine line
                    </span>
                  )}

                  {fineMode === "standard" && (
                    <Input
                      type="number"
                      value={standardLateFineAmount}
                      onChange={(e) => setStandardLateFineAmount(e.target.value)}
                      placeholder="Standard fine"
                      style={{ width: 130, fontSize: 12 }}
                    />
                  )}

        
                </div>
              </div>

              {eligibleForBatchWithFine.length === 0 ? (
                <div
                  style={{
                    padding: "20px 16px",
                    color: "#6b7280",
                    fontSize: 13,
                    textAlign: "center",
                  }}
                >
                  No pending or overdue instalments found.
                </div>
              ) : (
                <div style={{ padding: "0 16px 16px" }}>
                  {/* Header row */}
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: fineMode === "weekly" ? "32px 1fr 110px 110px 90px 120px 110px" : "32px 1fr 110px 110px 90px 110px",
                      gap: 8,
                      padding: "10px 0 6px",
                      borderBottom: "1px solid #f3d6d6",
                      marginBottom: 4,
                    }}
                  >
                    <input
                      type="checkbox"
                      style={{
                        width: 15,
                        height: 15,
                        accentColor: "#9f3a38",
                        marginTop: 2,
                      }}
                      checked={
                        pendingDuesFineSelection.length === eligibleForBatchWithFine.length &&
                        eligibleForBatchWithFine.length > 0
                      }
                      onChange={(e) =>
                        setPendingDuesFineSelection(
                          e.target.checked
                            ? eligibleForBatchWithFine.map((x) => x.key)
                            : []
                        )
                      }
                    />

                    <span style={{ fontSize: 11, fontWeight: 600, color: "#6b7280", textTransform: "uppercase" }}>
                      Instalment
                    </span>

                    <span style={{ fontSize: 11, fontWeight: 600, color: "#6b7280", textTransform: "uppercase" }}>
                      Due Date
                    </span>

                    <span style={{ fontSize: 11, fontWeight: 600, color: "#6b7280", textTransform: "uppercase", textAlign: "right" }}>
                      Pending
                    </span>

                    <span style={{ fontSize: 11, fontWeight: 600, color: "#6b7280", textTransform: "uppercase", textAlign: "right" }}>
                      Late Weeks
                    </span>

                    {fineMode === "weekly" && (
                      <span style={{ fontSize: 11, fontWeight: 600, color: "#6b7280", textTransform: "uppercase", textAlign: "right" }}>
                        Auto Fine
                      </span>
                    )}

                    <span style={{ fontSize: 11, fontWeight: 600, color: "#6b7280", textTransform: "uppercase", textAlign: "right" }}>
                      Line Total
                    </span>
                  </div>

                  {/* Instalment rows */}
                  {eligibleForBatchWithFine.map((item, rowIdx) => {
                    const isSelected = pendingDuesFineSelection.includes(item.key);
                    const rowTotal = fineMode === "weekly"
                      ? item.totalWithItemFine
                      : item.pendingAmount;

                    return (
                      <div
                        key={item.key}
                        onClick={() =>
                          setPendingDuesFineSelection((prev) =>
                            prev.includes(item.key)
                              ? prev.filter((k) => k !== item.key)
                              : [...prev, item.key]
                          )
                        }
                        style={{
                          display: "grid",
                          gridTemplateColumns: fineMode === "weekly" ? "32px 1fr 110px 110px 90px 120px 110px" : "32px 1fr 110px 110px 90px 110px",
                          gap: 8,
                          padding: "9px 4px",
                          borderBottom:
                            rowIdx < eligibleForBatchWithFine.length - 1
                              ? "1px solid #f6eeee"
                              : "none",
                          alignItems: "center",
                          cursor: "pointer",
                          background: isSelected ? "#faf7f7" : "transparent",
                          borderRadius: 6,
                          marginBottom: 2,
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => {}}
                          style={{
                            width: 15,
                            height: 15,
                            accentColor: "#9f3a38",
                          }}
                        />

                        <span style={{ fontSize: 13, fontWeight: 500 }}>
                          {item.label}
                          {item.isUpcoming && (
                            <span
                              style={{
                                marginLeft: 6,
                                fontSize: 11,
                                background: "#dbeafe",
                                color: "#1e40af",
                                padding: "1px 7px",
                                borderRadius: 20,
                              }}
                            >
                              next upcoming
                            </span>
                          )}
                        </span>

                        <span style={{ fontSize: 13, color: "#374151" }}>
                          {item.installment.dueDate || "-"}
                        </span>

                        <span style={{ fontSize: 13, fontWeight: 600, textAlign: "right" }}>
                          ${Number(item.pendingAmount || 0).toFixed(2)}
                        </span>

                        <span
                          style={{
                            fontSize: 13,
                            fontWeight: 600,
                            textAlign: "right",
                            color: item.lateWeeks > 0 ? "#7a3b3b" : "#6b7280",
                          }}
                        >
                          {item.lateWeeks}
                        </span>

                        {fineMode === "weekly" && (
                          <div onClick={(e) => e.stopPropagation()}>
                            <div style={{ textAlign: "right", fontSize: 13, fontWeight: 700, color: item.weeklyFineAmount > 0 ? "#9f3a38" : "#6b7280" }}>
                              ${Number(item.weeklyFineAmount || 0).toFixed(2)}
                            </div>
                            {fineMode === "weekly" && item.lateWeeks > 0 && (
                              <label style={{ display: "flex", justifyContent: "flex-end", gap: 5, fontSize: 11, color: "#6b7280", marginTop: 4 }}>
                                <input
                                  type="checkbox"
                                  checked={isFineSelected(item.key)}
                                  onChange={(e) => toggleFineForKey(item.key, e.target.checked)}
                                />
                                Apply fine
                              </label>
                            )}
                          </div>
                        )}

                        <span
                          style={{
                            fontSize: 13,
                            fontWeight: 700,
                            textAlign: "right",
                          }}
                        >
                          ${Number(rowTotal || 0).toFixed(2)}
                        </span>
                      </div>
                    );
                  })}

                  {/* Preview summary */}
                  {pendingDuesFineSelection.length > 0 && (
                    <div
                      style={{
                        marginTop: 14,
                        padding: "12px 14px",
                        background: "#fff",
                        border: "1px solid #f3d6d6",
                        borderRadius: 8,
                      }}
                    >
                      <div
                        style={{
                          fontSize: 12,
                          color: "#6b7280",
                          marginBottom: 8,
                          fontWeight: 500,
                        }}
                      >
                        Invoice preview — {pendingDuesFineSelection.length} selected instalment
                        {pendingDuesFineSelection.length > 1 ? "s" : ""}
                      </div>

                      {selectedPendingFineItems.map((e, i) => (
                        <div
                          key={e.key}
                          style={{
                            fontSize: 13,
                            padding: "6px 0",
                            borderBottom: "1px dashed #e5e7eb",
                          }}
                        >
                          <div style={{ display: "flex", justifyContent: "space-between" }}>
                            <span>
                              {i + 1}. {e.label} — pending instalment
                            </span>
                            <strong>${Number(e.pendingAmount || 0).toFixed(2)}</strong>
                          </div>

                          {fineMode === "weekly" && e.weeklyFineAmount > 0 && (
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                color: "#7a3b3b",
                                marginTop: 2,
                                fontSize: 12,
                              }}
                            >
                              <span>
                                Included in final fine line: {e.lateWeeks} week
                                {e.lateWeeks > 1 ? "s" : ""} × ${FINE_AMOUNT_PER_WEEK}
                              </span>
                              <strong>${Number(e.weeklyFineAmount || 0).toFixed(2)}</strong>
                            </div>
                          )}
                        </div>
                      ))}

                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          fontSize: 14,
                          fontWeight: 700,
                          marginTop: 8,
                          paddingTop: 6,
                          borderTop: "2px solid #f3d6d6",
                        }}
                      >
                        <span>Pending instalment total</span>
                        <span>${Number(batchPendingSubtotal || 0).toFixed(2)}</span>
                      </div>

                      {batchFineTotal > 0 && (
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            fontSize: 14,
                            fontWeight: 700,
                            marginTop: 4,
                            color: "#7a3b3b",
                          }}
                        >
                          <span>
                            {fineMode === "standard" ? "Standard late fine" : "Late fine total"} — one final invoice line
                          </span>
                          <span>${Number(batchFineTotal || 0).toFixed(2)}</span>
                        </div>
                      )}

                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          fontSize: 15,
                          fontWeight: 800,
                          marginTop: 6,
                          color: "#1f2937",
                        }}
                      >
                        <span>Total invoice amount</span>
                        <span>${Number(batchPendingWithFineTotal || 0).toFixed(2)}</span>
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      gap: 10,
                      marginTop: 14,
                    }}
                  >
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => {
                        setSendPendingDuesFineMode(false);
                        setPendingDuesFineSelection([]);
                        setSelectedFineKeys([]);
                      }}
                    >
                      Cancel
                    </Button>

                    <Button
                      size="sm"
                      disabled={pendingDuesFineSelection.length === 0 || loading}
                      loading={loading}
                      onClick={handleSendPendingDuesWithFineInvoice}
                    >
                      <Mail style={{ width: 14, height: 14, marginRight: 5 }} />
                      Send pending dues invoice ({pendingDuesFineSelection.length})
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Installment cards */}
          <DragDropContext onDragEnd={(result) => {
            if (!result.destination) return;
            const updatedInstallments = Array.from(feesPlan.installments);
            const [moved] = updatedInstallments.splice(result.source.index, 1);
            updatedInstallments.splice(result.destination.index, 0, moved);
            setFeesPlan((prev) => ({ ...prev, installments: [...updatedInstallments] }));
          }}>
            <Droppable droppableId="installmentsList" mode="virtual"
              renderClone={(provided, snapshot, rubric) => {
                const inst = feesPlan.installments[rubric.source.index];
                return (
                  <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}
                    style={{ ...provided.draggableProps.style, background: "#f0f9ff", borderRadius: 8, marginBottom: 12 }}>
                    <InstallmentCard
                      idx={rubric.source.index} installment={inst}
                      commissionRate={feesPlan.commissionRate} onChange={handleInstallmentChange}
                      onGenerateInvoice={handleGenerateInstallmentInvoice}
                      onGenerateReceipt={handleGenerateReceipt}
                      highlight={amountMismatch && parseFloat(inst.amountPaid || 0) < parseFloat(inst.depositAmount || 0)}
                      customInstallments={feesPlan.customInstallments}
                    />
                  </div>
                );
              }}>
              {(provided) => (
                <div ref={provided.innerRef} {...provided.droppableProps}>
                  {feesPlan.installments.map((inst, idx) => (
                    <Draggable key={inst.id} draggableId={inst.id} index={idx}>
                      {(provided, snapshot) => (
                        <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}
                          style={{ ...provided.draggableProps.style, background: snapshot.isDragging ? "#f0f9ff" : "white", marginBottom: 12, borderRadius: 8, transition: "background 0.2s ease" }}>
                          <InstallmentCard
                            idx={idx} installment={inst}
                            commissionRate={feesPlan.commissionRate}
                            onChange={handleInstallmentChange}
                            onGenerateInvoice={handleGenerateInstallmentInvoice}
                            onGenerateReceipt={handleGenerateReceipt}
                            onRemove={feesPlan.customInstallments
                              ? (removeIdx) => {
                                const updated = [...feesPlan.installments];
                                updated.splice(removeIdx, 1);
                                setFeesPlan((prev) => ({ ...prev, installments: updated, numOfInstallments: updated.length }));
                              } : undefined}
                            highlight={amountMismatch && parseFloat(inst.amountPaid || 0) < parseFloat(inst.depositAmount || 0)}
                            customInstallments={feesPlan.customInstallments}
                          />
                        </div>
                      )}
                    </Draggable>
                  ))}
                </div>
              )}
            </Droppable>
          </DragDropContext>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", marginTop: 24, padding: 12, background: "#eef2ff", borderRadius: 8, gap: 16 }}>
            <div><strong>Total Commission:</strong> ${totalCommission}</div>
            <div><strong>Total Commission Due:</strong> ${totalCommissionDue}</div>
          </div>
        </div>
      )}
      {activeTab === "invoiceHistory" && (
        <div>
          <SectionTitle icon={Mail}>Invoice History</SectionTitle>

          {(!feesPlan.invoiceHistory || feesPlan.invoiceHistory.length === 0) ? (
            <div
              style={{
                background: "#f9fafb",
                border: "1px solid #e5e7eb",
                borderRadius: 10,
                padding: 16,
                color: "#6b7280",
              }}
            >
              No invoice history available yet.
            </div>
          ) : (
            <div style={{ display: "grid", gap: 16 }}>
              {feesPlan.invoiceHistory
                .slice()
                .reverse()
                .map((entry, idx) => {
                  const isFineInvoice =
                    entry.type === "combined_with_fine" ||
                    Number(entry.fineAmount || 0) > 0;

                  const pendingSubtotal =
                    entry.pendingSubtotal ??
                    (entry.items || []).reduce(
                      (sum, item) =>
                        sum +
                        Number(item.pendingAmount || item.amount || 0),
                      0
                    );

                  const fineAmount = Number(entry.fineAmount || 0);
                  const totalWithFine =
                    entry.totalWithFine ??
                    Number(pendingSubtotal || 0) + Number(fineAmount || 0);

                  return (
                    <div
                      key={`${entry.invoiceNumber}-${idx}`}
                      style={{
                        border: isFineInvoice
                          ? "1px solid #e7bcbc"
                          : "1px solid #e5e7eb",
                        borderRadius: 12,
                        background: "white",
                        overflow: "hidden",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          gap: 12,
                          padding: "12px 16px",
                          background: isFineInvoice ? "#faf7f7" : "#eef2ff",
                          borderBottom: "1px solid #e5e7eb",
                          flexWrap: "wrap",
                        }}
                      >
                        <div>
                          <div style={{ fontWeight: 600, color: "#1f2937" }}>
                            Invoice #{entry.invoiceNumber || "-"}
                          </div>

                          <div
                            style={{
                              fontSize: 12,
                              color: "#6b7280",
                              marginTop: 2,
                            }}
                          >
                            Sent on: {entry.sentDate || "-"}
                          </div>
                        </div>

                        <div
                          style={{
                            fontSize: 12,
                            fontWeight: 700,
                            textTransform: "capitalize",
                            background: isFineInvoice ? "#f3d6d6" : "#ddd6fe",
                            color: isFineInvoice ? "#7a3b3b" : "#4338ca",
                            padding: "4px 10px",
                            borderRadius: 999,
                          }}
                        >
                          {isFineInvoice ? "Invoice with fine" : entry.type || "invoice"}
                        </div>
                      </div>

                      <div style={{ padding: 16 }}>
                        {isFineInvoice && (
                          <div
                            style={{
                              display: "grid",
                              gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                              gap: 10,
                              marginBottom: 14,
                              padding: 12,
                              background: "#faf7f7",
                              border: "1px solid #f3d6d6",
                              borderRadius: 8,
                            }}
                          >
                            <div>
                              <div style={{ fontSize: 11, color: "#6b7280" }}>
                                Fine Mode
                              </div>
                              <strong style={{ color: "#7a3b3b", textTransform: "capitalize" }}>
                                {entry.fineMode || "-"}
                              </strong>
                            </div>

                            <div>
                              <div style={{ fontSize: 11, color: "#6b7280" }}>
                                Pending Subtotal
                              </div>
                              <strong>
                                ${Number(pendingSubtotal || 0).toFixed(2)}
                              </strong>
                            </div>

                            <div>
                              <div style={{ fontSize: 11, color: "#6b7280" }}>
                                Fine Amount
                              </div>
                              <strong style={{ color: "#7a3b3b" }}>
                                ${Number(fineAmount || 0).toFixed(2)}
                              </strong>
                            </div>

                            <div>
                              <div style={{ fontSize: 11, color: "#6b7280" }}>
                                Total With Fine
                              </div>
                              <strong>
                                ${Number(totalWithFine || 0).toFixed(2)}
                              </strong>
                            </div>

                            {entry.fineMode === "weekly" && (
                              <div>
                                <div style={{ fontSize: 11, color: "#6b7280" }}>
                                  Fine Policy
                                </div>
                                <strong>
                                  ${Number(entry.fineAmountPerWeek || 0).toFixed(2)} per week
                                </strong>
                              </div>
                            )}

                            {entry.fineMode === "standard" && (
                              <div>
                                <div style={{ fontSize: 11, color: "#6b7280" }}>
                                  Standard Fine
                                </div>
                                <strong style={{ color: "#7a3b3b" }}>
                                  ${Number(entry.standardFineAmount || 0).toFixed(2)}
                                </strong>
                              </div>
                            )}

                            <div style={{ gridColumn: "1 / -1" }}>
                              <div style={{ fontSize: 11, color: "#6b7280" }}>
                                Fine Description
                              </div>
                              <strong style={{ color: "#7a3b3b" }}>
                                {entry.fineDescription || "-"}
                              </strong>
                            </div>
                          </div>
                        )}

                        <table style={{ width: "100%", borderCollapse: "collapse" }}>
                          <thead>
                            <tr style={{ textAlign: "left", background: "#f9fafb" }}>
                              <th style={{ padding: 8 }}>Item</th>
                              <th style={{ padding: 8 }}>Due Date</th>
                              <th style={{ padding: 8, textAlign: "right" }}>
                                Pending Amount
                              </th>

                              {isFineInvoice && (
                                <>
                                  <th style={{ padding: 8, textAlign: "right" }}>
                                    Late Weeks
                                  </th>
                                  <th style={{ padding: 8, textAlign: "right" }}>
                                    Fine
                                  </th>
                                  <th style={{ padding: 8 }}>Fine Applied</th>
                                </>
                              )}

                              <th style={{ padding: 8 }}>Status</th>
                            </tr>
                          </thead>

                          <tbody>
                            {(entry.items || []).map((item, itemIdx) => {
                              const itemFineAmount = Number(
                                item.weeklyFineAmount || item.fineAmount || 0
                              );

                              const fineApplied =
                                isFineInvoice &&
                                (
                                  item.fineSelected === true ||
                                  itemFineAmount > 0 ||
                                  (
                                    entry.fineMode === "standard" &&
                                    Number(entry.fineAmount || 0) > 0
                                  )
                                );

                              return (
                                <tr
                                  key={`${item.key || itemIdx}-${itemIdx}`}
                                  style={{ borderTop: "1px solid #e5e7eb" }}
                                >
                                  <td style={{ padding: 8 }}>
                                    <div style={{ fontWeight: 600 }}>
                                      {item.label || "-"}
                                    </div>

                                    {isFineInvoice && item.fineSelected && (
                                      <div
                                        style={{
                                          fontSize: 11,
                                          color: "#7a3b3b",
                                          marginTop: 2,
                                        }}
                                      >
                                        Fine selected for this instalment
                                      </div>
                                    )}
                                  </td>

                                  <td style={{ padding: 8 }}>
                                    {item.dueDate || "-"}
                                  </td>

                                  <td style={{ padding: 8, textAlign: "right" }}>
                                    ${Number(item.pendingAmount || item.amount || 0).toFixed(2)}
                                  </td>

                                  {isFineInvoice && (
                                    <>
                                      <td style={{ padding: 8, textAlign: "right" }}>
                                        {Number(item.lateWeeks || 0)}
                                      </td>

                                      <td
                                        style={{
                                          padding: 8,
                                          textAlign: "right",
                                          color: itemFineAmount > 0 ? "#7a3b3b" : "#6b7280",
                                          fontWeight: itemFineAmount > 0 ? 700 : 400,
                                        }}
                                      >
                                        ${itemFineAmount.toFixed(2)}
                                      </td>

                                      <td style={{ padding: 8 }}>
                                        {fineApplied ? (
                                          <span
                                            style={{
                                              color: "#7a3b3b",
                                              fontWeight: 700,
                                            }}
                                          >
                                            Yes
                                          </span>
                                        ) : (
                                          <span style={{ color: "#6b7280" }}>
                                            No
                                          </span>
                                        )}
                                      </td>
                                    </>
                                  )}

                                  <td style={{ padding: 8 }}>
                                    <StatusBadge status={item.status || "Pending"} />
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>

                        {isFineInvoice && (
                          <div
                            style={{
                              marginTop: 14,
                              paddingTop: 10,
                              borderTop: "2px solid #f3d6d6",
                              display: "grid",
                              gap: 6,
                              maxWidth: 360,
                              marginLeft: "auto",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                fontSize: 13,
                              }}
                            >
                              <span>Pending Instalment Total</span>
                              <strong>
                                ${Number(pendingSubtotal || 0).toFixed(2)}
                              </strong>
                            </div>

                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                fontSize: 13,
                                color: "#7a3b3b",
                              }}
                            >
                              <span>
                                {entry.fineMode === "standard"
                                  ? "Standard Late Fine"
                                  : "Late Fine Total"}
                              </span>
                              <strong>
                                ${Number(fineAmount || 0).toFixed(2)}
                              </strong>
                            </div>

                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                fontSize: 15,
                                fontWeight: 800,
                                color: "#1f2937",
                              }}
                            >
                              <span>Total Invoice Amount</span>
                              <strong>
                                ${Number(totalWithFine || 0).toFixed(2)}
                              </strong>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
            </div>
          )}
        </div>
      )}
      {activeTab === "receiptHistory" && (
        <div>
          <SectionTitle icon={Mail}>Receipt History</SectionTitle>

          {(!feesPlan.receiptHistory || feesPlan.receiptHistory.length === 0) ? (
            <div
              style={{
                background: "#f9fafb",
                border: "1px solid #e5e7eb",
                borderRadius: 10,
                padding: 16,
                color: "#6b7280",
              }}
            >
              No receipt history available yet.
            </div>
          ) : (
            <div style={{ display: "grid", gap: 16 }}>
              {feesPlan.receiptHistory
                .slice()
                .reverse()
                .map((entry, idx) => (
                  <div
                    key={`${entry.receiptNumber}-${idx}`}
                    style={{
                      border: "1px solid #e5e7eb",
                      borderRadius: 12,
                      background: "white",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 12,
                        padding: "12px 16px",
                        background: "#eef2ff",
                        borderBottom: "1px solid #e5e7eb",
                        flexWrap: "wrap",
                      }}
                    >
                      <div>
                        <div style={{ fontWeight: 600, color: "#1f2937" }}>
                          Receipt #{entry.receiptNumber || "-"}
                        </div>
                        <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
                          Sent on: {entry.sentDate || "-"}
                        </div>
                      </div>

                      <div
                        style={{
                          fontSize: 12,
                          fontWeight: 600,
                          textTransform: "capitalize",
                          background: "#ddd6fe",
                          color: "#4338ca",
                          padding: "4px 10px",
                          borderRadius: 999,
                        }}
                      >
                        {entry.type || "receipt"}
                      </div>
                    </div>

                    <div style={{ padding: 16 }}>
                      <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <thead>
                          <tr style={{ textAlign: "left", background: "#f9fafb" }}>
                            <th style={{ padding: 8 }}>Item</th>
                            <th style={{ padding: 8 }}>Due Date</th>
                            <th style={{ padding: 8 }}>Paid Date</th>
                            <th style={{ padding: 8 }}>Amount</th>
                            <th style={{ padding: 8 }}>Invoice</th>
                            <th style={{ padding: 8 }}>Status</th>
                          </tr>
                        </thead>

                        <tbody>
                          {(entry.items || []).map((item, itemIdx) => (
                            <tr
                              key={`${item.key || itemIdx}-${itemIdx}`}
                              style={{ borderTop: "1px solid #e5e7eb" }}
                            >
                              <td style={{ padding: 8 }}>{item.label || "-"}</td>
                              <td style={{ padding: 8 }}>{item.dueDate || "-"}</td>
                              <td style={{ padding: 8 }}>{item.paidDate || "-"}</td>
                              <td style={{ padding: 8 }}>
                                ${parseFloat(item.amount || 0).toFixed(2)}
                              </td>
                              <td style={{ padding: 8 }}>{item.invoiceNumber || "-"}</td>
                              <td style={{ padding: 8 }}>
                                <StatusBadge status={item.status || "Paid"} />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>
      )}

      {/* Modal */}
      {modalOpen && (
        <Modal title="Calculate Installments" onClose={() => setModalOpen(false)}>
          <FormField label="Start Date">
            <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </FormField>
          <FormField label="Frequency">
            <Select value={frequency} onChange={(e) => setFrequency(e.target.value)}>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
            </Select>
          </FormField>
          <Button onClick={handlePreview}>Preview</Button>
          {preview.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <h4>Preview Due Dates:</h4>
              {preview.map((inst, idx) => (
                <div key={idx}>Installment #{idx + 1}: {inst.dueDate}</div>
              ))}
              <Button style={{ marginTop: 12 }} onClick={applyPreview}>Apply to Plan</Button>
            </div>
          )}
        </Modal>
      )}

      {saveMessage && (
        <p style={{ marginTop: 12, color: saveMessage.includes("Failed") ? "red" : "green" }}>{saveMessage}</p>
      )}
    </div>
  );
}